import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";

/* ===========================
      DROPDOWN COMPONENT
=========================== */

const Dropdown = ({ title, items, iconClass, link, closeMenu }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const handleEnter = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timer.current = setTimeout(() => {
      setOpen(false);
    }, 180);
  };

  return (
    <li
      className="nav-item dropdown-parent"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <NavLink
        to={link}
        className={({ isActive }) =>
          `nav-link services-toggle ${isActive ? "active" : ""}`
        }
        onClick={closeMenu}
      >
        <span className="link-content">
          <i className={`${iconClass} menu-icon`}></i>
          {title}
        </span>

        <i className={`bi bi-chevron-down arrow ${open ? "rotate" : ""}`}></i>
      </NavLink>

      <ul className={`dropdown-menu ${open ? "show" : ""}`}>
        {items.map((item) => (
          <li key={item.id}>
            <Link to={`/services#${item.id}`} onClick={closeMenu}>
              {item.name}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

/* ===========================
      MEGA MENU
=========================== */

const MegaDropdown = ({ title, menu, closeMenu }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const handleEnter = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timer.current = setTimeout(() => {
      setOpen(false);
    }, 180);
  };

  return (
    <li
      className="nav-item mega-parent"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <span className="nav-link services-toggle">
        <span className="link-content">
          <i className="bi bi-cpu"></i>

          {title}
        </span>

        <i className={`bi bi-chevron-down arrow ${open ? "rotate" : ""}`}></i>
      </span>

      <div className={`mega-dropdown ${open ? "show" : ""}`}>
        <div className="mega-grid">
          {menu.map((section, index) => (
            <div className="mega-card" key={index}>
              <div className="mega-icon">
                <i className={section.icon}></i>
                <h3>{section.title}</h3>
              </div>

              <ul>
                {section.items.map((item, i) => (
                  <li key={i}>
                    <a href="#" onClick={closeMenu}>
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </li>
  );
};

/* ===========================
        NAVBAR
=========================== */

const Navbar = () => {
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  // ✅ ADD THIS (scroll hide/show)
  const [show, setShow] = useState(true);
  const [lastScroll, setLastScroll] = useState(0);

  const closeMenu = () => setMenuOpen(false);

  /* =========================
     SCROLL: hide/show navbar
  ========================= */
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;

      if (current > lastScroll) {
        setShow(false); // scroll down → hide
      } else {
        setShow(true); // scroll up → show
      }

      setLastScroll(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScroll]);

  /* =========================
     SCROLL: progress + background
  ========================= */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      const total = document.documentElement.scrollHeight - window.innerHeight;

      const current = (window.scrollY / total) * 100;

      setProgress(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const servicesItems = [
    { name: "Mobile Apps", id: "mobile" },
    { name: "Web Development", id: "web" },
    { name: "UI/UX Design", id: "uiux" },
    { name: "Digital Marketing", id: "marketing" },
  ];

  const technologyMenu = [
    {
      title: "Mobile",
      icon: "bi bi-phone",
      items: ["iOS", "Android", "Flutter", "Kotlin"],
    },
    {
      title: "Backend",
      icon: "bi bi-server",
      items: ["Node.js", "Php", "Python"],
    },
    {
      title: "Frontend",
      icon: "bi bi-code-slash",
      items: ["React.js", "Vue.js", "HTML5", "CSS3"],
    },
    {
      title: "Database",
      icon: "bi bi-database",
      items: ["MySQL", "MongoDB"],
    },
    {
      title: "UI/UX",
      icon: "bi bi-palette",
      items: [
        "Mobile App Design",
        "Website UI",
        "UX Optimization",
        "Responsive Design",
      ],
    },
    {
      title: "Marketing",
      icon: "bi bi-megaphone",
      items: ["SEO", "Social Media", "Google Ads", "Meta Ads"],
    },
  ];

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${progress}%` }} />

      {/* NAVBAR */}
      <nav
        className={`navbar ${show ? "show" : "hide"} ${scrolled ? "scrolled" : ""}`}
      >
        {/* LOGO */}
        <Link to="/" className="logo">
          {/* Lifetime <span>Infotech</span> */}
          <span className="text-gradient">LifeTime Infotech</span>
        </Link>

        {/* HAMBURGER */}
        <div
          className={`hamburger ${menuOpen ? "active" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* MENU */}
        <ul className={`navbar-menu ${menuOpen ? "active" : ""}`}>
          <li>
            <NavLink
              to="/home"
              onClick={closeMenu}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <i className="bi bi-house"></i>
              Home
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/about"
              onClick={closeMenu}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <i className="bi bi-info-circle"></i>
              About
            </NavLink>
          </li>

          <Dropdown
            title="Services"
            items={servicesItems}
            iconClass="bi bi-tools"
            link="/services"
            closeMenu={closeMenu}
          />

          <MegaDropdown
            title="Technology"
            menu={technologyMenu}
            closeMenu={closeMenu}
          />

          <li>
            <NavLink
              to="/portfolio"
              onClick={closeMenu}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <i className="bi bi-briefcase"></i>
              Portfolio
            </NavLink>
          </li>

          <li>
            <NavLink
              to="/contact"
              onClick={closeMenu}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <i className="bi bi-envelope"></i>
              Contact
            </NavLink>
          </li>
        </ul>
        {/* MOBILE OVERLAY (optional modern touch) */}
        {menuOpen && <div className="mobile-overlay" onClick={closeMenu} />}
      </nav>
    </>
  );
};

export default Navbar;
